import { nextTick, type Ref, type Reactive } from "vue";
import { useRouter } from "vue-router";
import { ScheduledDeliveryCreateInput } from "@/generated/graphql";
import { routerPath } from "@/common/router";
import { SCHEDULED_DELIVERY_LIST_FILE_NAME } from "@/views/admin/ScheduledDeliveryList/constants";
import type { ListData } from "../types";

/**
 * 店舗間定期配達登録 ボタン操作・画面遷移
 * 確認・登録・変更・削除・キャンセル・完了後の遷移を管理する
 */
export const useScheduledDeliveryRegisterActions = (deps: {
  overlay: Ref<boolean>;
  updateMode: Ref<boolean>;
  deleteMode: Ref<boolean>;
  displayConfirmDialog: Ref<boolean>;
  displayCompletedDialog: Ref<boolean>;
  hasAttemptedConfirm: Ref<boolean>;
  scheduledDeliveryCreateInput: Reactive<ScheduledDeliveryCreateInput>;
  deliverySelectValues: Ref<ListData | null | undefined>;
  deliveryRequestCreateTimeValues: Ref<number | null | undefined>;
  createDeliveryId: () => Promise<void>;
  copyCreateToUpdate: () => Promise<void>;
  allClear: () => Promise<void>;
  createScheduledDelivery: () => Promise<boolean>;
  updateScheduledDelivery: () => Promise<boolean>;
  deleteScheduledDelivery: () => Promise<boolean>;
}) => {
  const {
    overlay,
    updateMode,
    deleteMode,
    displayConfirmDialog,
    displayCompletedDialog,
    hasAttemptedConfirm,
    scheduledDeliveryCreateInput,
    deliverySelectValues,
    deliveryRequestCreateTimeValues,
    createDeliveryId,
    copyCreateToUpdate,
    allClear,
    createScheduledDelivery,
    updateScheduledDelivery,
    deleteScheduledDelivery,
  } = deps;

  const router = useRouter();

  /**
   * 「登録手続きへ(確認)」ボタン @Click
   */
  async function onClickOpenConfirmDialog(): Promise<void> {
    hasAttemptedConfirm.value = true;
    // 店舗登録確認ダイアログ 表示
    displayConfirmDialog.value = true;
  }

  /**
   * 「戻る」ボタン @Click
   */
  async function onClickInputCancel(): Promise<void> {
    nextTick(async () => {
      //入力項目をクリア
      await allClear();
      hasAttemptedConfirm.value = false;
      // 「店舗間定期配達登録」画面へ遷移
      await router.push({ path: routerPath.scheduledDeliveryList });
    });
  }

  /**
   * 一覧へ戻る @Click
   */
  async function onClickBack(): Promise<void> {
    // 店舗間定期配達登録確認ダイアログ 非表示
    displayConfirmDialog.value = false;
    await router.push({
      name: SCHEDULED_DELIVERY_LIST_FILE_NAME,
      query: { searchMode: "true" },
    });
  }

  /**
   * 店舗間定期配達を登録する @Click
   */
  async function onClickConfirmEntry(): Promise<void> {
    overlay.value = true;

    try {
      // お届け日設定
      await createDeliveryId();

      // 登録内容設定
      // エリアコード
      scheduledDeliveryCreateInput.area_code = deliverySelectValues.value
        ? deliverySelectValues.value?.area_code
        : "";
      // 便ID
      scheduledDeliveryCreateInput.delivery_id = deliverySelectValues.value
        ? Number(deliverySelectValues.value?.delivery_id)
        : 0;
      // 配達依頼作成時間
      scheduledDeliveryCreateInput.delivery_request_create_time = String(
        deliveryRequestCreateTimeValues.value
      ).padStart(2, "0") + ":00:00";
      // 定期配達有効期間（開始）
      if (
        scheduledDeliveryCreateInput.valid_date_from == null ||
        scheduledDeliveryCreateInput.valid_date_from == ""
      ) {
        scheduledDeliveryCreateInput.valid_date_from = undefined;
      }
      // 定期配達有効期間（終了）
      if (
        scheduledDeliveryCreateInput.valid_date_to == null ||
        scheduledDeliveryCreateInput.valid_date_to == ""
      ) {
        scheduledDeliveryCreateInput.valid_date_to = undefined;
      }
      // 配達備考
      if (
        scheduledDeliveryCreateInput.delivery_notes == null ||
        scheduledDeliveryCreateInput.delivery_notes == ""
      ) {
        scheduledDeliveryCreateInput.delivery_notes = undefined;
      }

      if (!updateMode.value) {
        // 店舗間定期配達作成
        if (!(await createScheduledDelivery())) {
          return;
        }

        // 店舗間定期配達登録確認ダイアログ 非表示
        displayConfirmDialog.value = false;

        nextTick(async () => {
          // 店舗間定期配達登録完了ダイアログ 表示
          displayCompletedDialog.value = true;
          overlay.value = false;
        });
      } else {
        // 入力値のコピー
        await copyCreateToUpdate();

        // 店舗間定期配達変更
        if (!(await updateScheduledDelivery())) {
          return;
        }

        // 一覧に戻る
        await router.push({
          name: SCHEDULED_DELIVERY_LIST_FILE_NAME,
          query: { searchMode: "true" },
        });
      }
    } finally {
      overlay.value = false;
    }
  }

  /**
   * 店舗間定期配達を削除する @Click
   */
  async function onClickDelete(): Promise<void> {
    // 店舗間定期配達変更
    if (!(await deleteScheduledDelivery())) {
      return;
    }

    // 一覧に戻る
    await router.push({
      name: SCHEDULED_DELIVERY_LIST_FILE_NAME,
      query: { searchMode: "true" },
    });
  }

  /**
   * キャンセル @Click
   */
  async function onClickConfirmCancel(): Promise<void> {
    if (deleteMode.value) {
      // 一覧に戻る
      await router.push({
        name: SCHEDULED_DELIVERY_LIST_FILE_NAME,
        query: { searchMode: "true" },
      });
    } else {
      // 店舗間定期配達登録確認ダイアログ 非表示
      displayConfirmDialog.value = false;
    }
  }

  /**
   * 戻る @Click
   */
  async function onClickCompletedGoStart(): Promise<void> {
    // 店舗間定期配達登録完了ダイアログ 非表示
    displayCompletedDialog.value = false;

    nextTick(async () => {
      //入力項目をクリア
      await allClear();
      hasAttemptedConfirm.value = false;
      // 「店舗間定期配達登録」画面へ遷移
      await router.push({ path: routerPath.scheduledDeliveryList });
    });
  }

  return {
    onClickOpenConfirmDialog,
    onClickInputCancel,
    onClickBack,
    onClickConfirmEntry,
    onClickDelete,
    onClickConfirmCancel,
    onClickCompletedGoStart,
  };
};
